import React, { useState } from "react";
import { X, Trash2, Plus, Info } from "lucide-react";
import { useSelector } from "react-redux";

interface BatteryRow {
  id: string;
  startDate: string;
  endDate: string;
  quantum: string;
  chargingRate: string;
  vc: string; // Variable Cost
  fc: string; // Fixed Cost
  cycles?: string;
  efficiency?: string;
}

interface AddBatteryModalProps {
  isModalOpen: boolean;
  setIsModalOpen: (open: boolean) => void;
  modalTitle?: string;
  onSubmit: (data: BatteryRow[]) => void;
  loading: any;
  setLoading: any;
}

const AddBatteryModal: React.FC<AddBatteryModalProps> = ({
  isModalOpen,
  setIsModalOpen,
  modalTitle = "Add Battery",
  onSubmit,
  loading,
  setLoading,
}) => {
  const SelectedStartDate = useSelector(
    (state: any) => state.SurplusOptimizerHPPCDate.startDate
  );
  const SelectedEndDate = useSelector(
    (state: any) => state.SurplusOptimizerHPPCDate.endDate
  );


  const [batteries, setBatteries] = useState<BatteryRow[]>([
    {
      id: "1",
      startDate: "",
      endDate: "",
      quantum: "",
      chargingRate: "",
      vc: "",
      fc: "",
      cycles: "",
      efficiency: "",
    },
  ]);

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showErrors, setShowErrors] = useState(false);

  const validateBatteries = () => {
    const newErrors: Record<string, string> = {};

    batteries.forEach((battery) => {
      if (!battery.startDate) {
        newErrors[`${battery.id}-startDate`] = "Required";
      }
      if (!battery.endDate) {
        newErrors[`${battery.id}-endDate`] = "Required";
      }
      if (!battery.quantum) {
        newErrors[`${battery.id}-quantum`] = "Required";
      }
      if (!battery.chargingRate) {
        newErrors[`${battery.id}-chargingRate`] = "Required";
      }
      if (!battery.vc) {
        newErrors[`${battery.id}-vc`] = "Required";
      }
      // ❌ FC validation removed
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleAddBattery = () => {
    const newBattery: BatteryRow = {
      id: Date.now().toString(),
      startDate: "",
      endDate: "",
      quantum: "",
      chargingRate: "",
      vc: "",
      fc: "",
      cycles: "",
      efficiency: "",
    };
    setBatteries([...batteries, newBattery]);
  };

  const handleDeleteBattery = (id: string) => {
    if (batteries.length > 1) {
      setBatteries(batteries.filter((battery) => battery.id !== id));
    }
  };

  const handleUpdateBattery = (id: string, field: string, value: any) => {
    setBatteries(
      batteries.map((battery) => {
        if (battery.id === id) {
          // If updating start date and end date is before it, clear end date
          if (field === "startDate" && battery.endDate && value > battery.endDate) {
            return { ...battery, [field]: value, endDate: "" };
          }
          return { ...battery, [field]: value };
        }
        return battery;
      })
    );
    if (errors[`${id}-${field}`]) {
      const newErrors = { ...errors };
      delete newErrors[`${id}-${field}`];
      setErrors(newErrors);
    }
  };

  const handleSubmit = () => {
    setShowErrors(true);
    if (validateBatteries()) {
      onSubmit(batteries);
      setShowErrors(false);
      setLoading(true);
      // Reset to initial state after submit
      setBatteries([
        {
          id: "1",
          startDate: "",
          endDate: "",
          quantum: "",
          chargingRate: "",
          vc: "",
          fc: "",
          cycles: "",
          efficiency: "",
        },
      ]);
      setErrors({});
    }
  };

  const handleClose = () => {
    setIsModalOpen(false);
    setShowErrors(false);
    // Reset to initial state on close
    setBatteries([
      {
        id: "1",
        startDate: "",
        endDate: "",
        quantum: "",
        chargingRate: "",
        vc: "",
        fc: "",
        cycles: "",
        efficiency: "",
      },
    ]);
    setErrors({});
  };

  if (!isModalOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[99] p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-4 py-2 border-b border-gray-200 flex items-center justify-between bg-white">
          <h2 className="text-base font-medium text-[#525062]">{modalTitle}</h2>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Close modal"
          >
            <X strokeWidth={3} size={18} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-4 py-4 bg-[#F3F5F8]">
          {showErrors && Object.keys(errors).length > 0 && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-600 text-xs font-medium">
                Please fill all required fields
              </p>
            </div>
          )}
          <div className="space-y-4">
            {batteries.map((battery, batteryIndex) => (
              <div key={battery.id} className="bg-white rounded-lg">
                {/* Header with Battery Number and Delete */}
                <div className="flex items-center justify-between w-[100%] px-3 py-2 mb-2 border-b-2 border-[#DFEAF2]">
                  <h3 className="text-xs font-medium text-[#34656D]">
                    Battery {batteryIndex + 1}
                  </h3>
                  <div className="flex gap-2 mr-3">
                    <button
                      onClick={() => handleDeleteBattery(battery.id)}
                      className="p-1 rounded-lg border border-[#E2E2E5] bg-[#F3F3F4] cursor-pointer hover:bg-gray-100 transition-colors text-gray-400"
                      aria-label="Delete battery"
                      disabled={batteries.length === 1}
                    >
                      <Trash2 strokeWidth={3} size={16} />
                    </button>
                    <button
                      onClick={handleAddBattery}
                      className="p-1 rounded-lg border border-[#5B8A9F] bg-[#E8F4F8] cursor-pointer hover:bg-blue-50 transition-colors text-[#5B8A9F]"
                      aria-label="Add battery"
                    >
                      <Plus strokeWidth={3} size={16} />
                    </button>
                  </div>
                </div>
                <div className="px-3 py-2">
                  {/* First Row: Start Date, End Date */}
                  <div className="grid grid-cols-2 gap-3 mb-4">
                    <div>
                      <label className="flex items-center gap-1 text-xs font-medium mb-1 text-[#6F6D7C]">
                        Start Date <span className="text-red-500">*</span>
                        <Info size={12} className="text-gray-400" />
                      </label>
                      <input
                        type="date"
                        value={battery.startDate}
                        onChange={(e) =>
                          handleUpdateBattery(
                            battery.id,
                            "startDate",
                            e.target.value
                          )
                        }
                        min={SelectedStartDate}
                        max={SelectedEndDate}
                        className={`w-full px-2 py-1.5 border rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-gray-700 ${showErrors && errors[`${battery.id}-startDate`]
                          ? "border-red-500"
                          : "border-[#E2E2E5]"
                          }`}
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-medium mb-1 text-[#6F6D7C]">
                        End Date <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="date"
                        value={battery.endDate}
                        onChange={(e) =>
                          handleUpdateBattery(battery.id, "endDate", e.target.value)
                        }
                        min={
                          battery.startDate ? battery.startDate : SelectedStartDate
                        }
                        max={SelectedEndDate}
                        className={`w-full px-2 py-1.5 border rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-700 ${showErrors && errors[`${battery.id}-endDate`]
                          ? "border-red-500"
                          : "border-[#E2E2E5]"
                          }`}
                      />
                    </div>
                  </div>

                  {/* Second Row: Quantum, Charging Rate, VC, FC */}
                  <div className="grid grid-cols-3 gap-3 mb-3">
                    <div>
                      <label className="block text-xs font-medium mb-1 text-[#6F6D7C]">
                        Quantum (MWh) <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="number"
                        value={battery.quantum}
                        onChange={(e) =>
                          handleUpdateBattery(
                            battery.id,
                            "quantum",
                            e.target.value
                          )
                        }
                        placeholder="Enter quantum"
                        className={`w-full px-2 py-1.5 border rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-gray-700 ${showErrors && errors[`${battery.id}-quantum`]
                          ? "border-red-500"
                          : "border-[#E2E2E5]"
                          }`}
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-medium mb-1 text-[#6F6D7C]">
                        Charging Rate (MW) <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        value={battery.chargingRate}
                        onChange={(e) =>
                          handleUpdateBattery(battery.id, "chargingRate", e.target.value)
                        }
                        placeholder="Charging Rate"
                        className={`w-full px-2 py-1.5 border rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-gray-700 ${showErrors && errors[`${battery.id}-chargingRate`]
                          ? "border-red-500"
                          : "border-[#E2E2E5]"
                          }`}
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-medium mb-1 text-[#6F6D7C]">
                        VC (₹/kWh) <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        value={battery.vc}
                        onChange={(e) =>
                          handleUpdateBattery(battery.id, "vc", e.target.value)
                        }
                        placeholder="Variable Cost"
                        className={`w-full px-2 py-1.5 border rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-gray-700 ${showErrors && errors[`${battery.id}-vc`]
                          ? "border-red-500"
                          : "border-[#E2E2E5]"
                          }`}
                      />
                    </div>
                  </div>

                  {/* Third Row: FC, Cycles, Efficiency (Read-only) */}
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <label className="block text-xs font-medium mb-1 text-[#6F6D7C]">
                        FC (₹/kW)
                      </label>
                      <div className="w-full px-2 py-1.5 border rounded-lg bg-gray-50 text-xs text-gray-700 border-[#E2E2E5]">
                        {battery.fc || "-"}
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs font-medium mb-1 text-[#6F6D7C]">
                        Cycles No.
                      </label>
                      <div className="w-full px-2 py-1.5 border rounded-lg bg-gray-50 text-xs text-gray-700 border-[#E2E2E5]">
                        {battery.cycles || "-"}
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs font-medium mb-1 text-[#6F6D7C]">
                        Efficiency
                      </label>
                      <div className="w-full px-2 py-1.5 border rounded-lg bg-gray-50 text-xs text-gray-700 border-[#E2E2E5]">
                        {battery.efficiency || "-"}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="px-4 py-3 border-t border-gray-200 flex justify-center gap-3 bg-white">
          <button
            onClick={handleClose}
            className="flex items-center justify-center px-6 py-1.5 border border-[#8C8A96] text-[#8C8A96] text-sm rounded-xl font-medium hover:opacity-90 transition-opacity disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading}
            className="flex items-center justify-center px-6 py-1.5 bg-[#5B8A9F] text-white text-sm rounded-xl font-medium hover:opacity-90 transition-opacity disabled:opacity-50"
          >
            {loading ? "Loading..." : "Submit"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddBatteryModal;
export { AddBatteryModal };
export type { BatteryRow };